import { useState, useEffect } from 'react';
import { usePublisherStore } from '.';

function usePublisher() {
    const publisherStore = usePublisherStore();

    const [featuredPublishers, setFeaturedPublishers] = useState([]);
    const [featuredSection, setFeaturedSection] = useState({});
    const [whatshotPublishers, setWhatshotPublishers] = useState([]);
    const [whatsHotPublisherIds, setWhatsHotPublisherIds] = useState({});
    const [popularNowPublishers, setPopularNowPublishers] = useState([]);
    const [nearMePublishers, setNearMePublishers] = useState([]);
    const [recommendedPublishers, setRecommendedPublishers] = useState([]);
    const [directoryPublishers, setDirectoryPublishers] = useState([]);
    const [searchDirectoryResults, setSearchDirectoryResults] = useState([]);
    const [filteredDirectoryPublishers, setFilteredDirectoryPublishers] = useState([]);
    const [directoryFilterIndustries, setDirectoryFilterIndustries] = useState([]);
    const [directoryHasMore, setDirectoryHasMore] = useState(true);
    const [searchHasMore, setSearchHasMore] = useState(true);
    const [search, setSearch] = useState({
        whatshotEnabled: false,
        searchText: '',
        industryFilters: [],
    });

    // Plain (unmemoized) read, not wrapped in the now-deprecated useObserver —
    // every caller of usePublisher() is itself observer()-wrapped, so this
    // read is tracked as part of that caller's own render, the same way any
    // other observable read inside a called hook would be.
    const signals = {
        featuredPublishers: publisherStore.observed.featuredPublishers,
        whatshotPublishers: publisherStore.observed.whatshotPublishers,
        popularNowPublishers: publisherStore.observed.popularNowPublishers,
        nearMePublishers: publisherStore.observed.nearMePublishers,
        recommendedPublishers: publisherStore.observed.recommendedPublishers,
        directoryPublishers: publisherStore.observed.directoryPublishers,
        searchDirectoryResults: publisherStore.observed.searchDirectoryResults,
        filteredDirectoryPublishers: publisherStore.observed.filteredDirectoryPublishers,
        search: publisherStore.observed.search,
        // Read raw (not a uuid token) — booleans are reactive as-is.
        featuredPublishersLoading: publisherStore.observed.featuredPublishersLoading,
        whatshotPublishersLoading: publisherStore.observed.whatshotPublishersLoading,
        popularNowPublishersLoading: publisherStore.observed.popularNowPublishersLoading,
        nearMePublishersLoading: publisherStore.observed.nearMePublishersLoading,
        recommendedPublishersLoading: publisherStore.observed.recommendedPublishersLoading,
        directoryPublishersLoading: publisherStore.observed.directoryPublishersLoading,
        directoryPublishersLoadingMore: publisherStore.observed.directoryPublishersLoadingMore,
        directoryPublishersSearching: publisherStore.observed.directoryPublishersSearching,
        directoryPublishersSearchingMore: publisherStore.observed.directoryPublishersSearchingMore,
    };

    useEffect(() => {
        setFeaturedPublishers(publisherStore.featuredPublishers);
        setFeaturedSection(publisherStore.featuredSection);
    }, [signals.featuredPublishers]);

    useEffect(() => {
        setWhatshotPublishers(publisherStore.whatshotPublishers);
        setWhatsHotPublisherIds(publisherStore.whatsHotPublisherIds);
    }, [signals.whatshotPublishers]);

    useEffect(() => {
        setPopularNowPublishers(publisherStore.popularNowPublishers);
    }, [signals.popularNowPublishers]);

    useEffect(() => {
        setNearMePublishers(publisherStore.nearMePublishers);
    }, [signals.nearMePublishers]);

    useEffect(() => {
        setRecommendedPublishers(publisherStore.recommendedPublishers);
    }, [signals.recommendedPublishers]);

    useEffect(() => {
        setDirectoryPublishers(publisherStore.directoryPublishers);
        setDirectoryFilterIndustries(publisherStore.directoryFilterIndustries);
        // directoryHasMore changes at the same time as the lists above (both
        // getDirectoryPublishers and loadMoreDirectoryPublishers update it
        // right before bumping this same token), so it rides along here
        // rather than needing its own observed token.
        setDirectoryHasMore(publisherStore.directoryHasMore);
    }, [signals.directoryPublishers]);

    // Search mode's own pool/cursor (EBL-13117) — separate token, separate
    // effect, mirroring the browse-mode one above.
    useEffect(() => {
        setSearchDirectoryResults(publisherStore.searchDirectoryResults);
        setDirectoryFilterIndustries(publisherStore.directoryFilterIndustries);
        setSearchHasMore(publisherStore.searchHasMore);
    }, [signals.searchDirectoryResults]);

    useEffect(() => {
        setFilteredDirectoryPublishers(publisherStore.filteredDirectoryPublishers);
        // applyDirectoryFilter() recomputes directoryFilterIndustries from
        // whichever pool (browse/search) is active every time it runs —
        // including on a search-text clear, which doesn't bump either of
        // the two effects above — so it has to be re-read here too.
        setDirectoryFilterIndustries(publisherStore.directoryFilterIndustries);
    }, [signals.filteredDirectoryPublishers]);

    useEffect(() => {
        setSearch({ ...publisherStore.search });
    }, [signals.search]);

    return {
        featuredPublishers,
        featuredSection,
        whatshotPublishers,
        whatsHotPublisherIds,
        popularNowPublishers,
        nearMePublishers,
        recommendedPublishers,
        directoryPublishers,
        searchDirectoryResults,
        directoryFilterIndustries,
        directoryHasMore,
        searchHasMore,
        filteredDirectoryPublishers,
        search,
        featuredPublishersLoading: signals.featuredPublishersLoading,
        whatshotPublishersLoading: signals.whatshotPublishersLoading,
        popularNowPublishersLoading: signals.popularNowPublishersLoading,
        nearMePublishersLoading: signals.nearMePublishersLoading,
        recommendedPublishersLoading: signals.recommendedPublishersLoading,
        directoryPublishersLoading: signals.directoryPublishersLoading,
        directoryPublishersLoadingMore: signals.directoryPublishersLoadingMore,
        directoryPublishersSearching: signals.directoryPublishersSearching,
        directoryPublishersSearchingMore: signals.directoryPublishersSearchingMore,
    };
}

export default usePublisher;
